import { useEffect, useRef } from "react";
import { useRouter } from "@tanstack/react-router";

interface Props {
  trackingId: string;
}

/**
 * Google Analytics 4 integration.
 * Injects the gtag.js loader once on mount, then fires a `page_view` event
 * on every client-side route change (TanStack Router SPA navigation).
 *
 * Usage: <GoogleAnalytics trackingId="G-YH39LT89KG" /> inside the app root.
 */
export function GoogleAnalytics({ trackingId }: Props) {
  const router = useRouter();
  const injected = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (injected.current) return;
    injected.current = true;

    // 1) Load the gtag.js library.
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    document.head.appendChild(script);

    // 2) Bootstrap the dataLayer + gtag stub.
    const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
    w.dataLayer = w.dataLayer || [];
    w.gtag = function gtag() { w.dataLayer!.push(arguments); };
    w.gtag("js", new Date());
    // send_page_view: false — we send it manually below on each route change,
    // so single-page navigations are counted correctly (once).
    w.gtag("config", trackingId, { send_page_view: false });

    // 3) Track the initial page view.
    w.gtag("event", "page_view", {
      page_path: window.location.pathname + window.location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [trackingId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const unsub = router.subscribe("onResolved", () => {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (!w.gtag) return;
      w.gtag("event", "page_view", {
        page_path: window.location.pathname + window.location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    });
    return () => { unsub(); };
  }, [router]);

  return null;
}
